import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { removeToast } from '../../../store/notificationSlice';
import {
  invokeNotificationCallback,
  clearNotificationCallback,
} from '../../../util/notificationCallbacks';
import classes from './Toast.module.css';

const ToastIcon = ({ type }) => {
  if (type === 'success') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 12.2L10.6 14.8L16 9.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'network' || type === 'warning') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 8V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="16.5" r="1" fill="currentColor" />
        <path d="M10.3 4.7C11.2 4.25 12 4 12 4C12 4 16.5 5.8 19.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6.5 7.5C8.1 6.1 10 5 12 4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M3.5 12C4.4 10.4 5.8 9 7.5 7.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'info') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 10V17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="7.5" r="1" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.5V13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
    </svg>
  );
};

const ToastItem = ({ toast, onDismiss }) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLeaving(true);
      clearNotificationCallback(toast.actionCallbackId);
      setTimeout(() => dismissRef.current(toast.id), 180);
    }, toast.duration);

    return () => clearTimeout(timeout);
  }, [toast.duration, toast.id]);

  const handleAction = () => {
    invokeNotificationCallback(toast.actionCallbackId);
    setIsLeaving(true);
    setTimeout(() => onDismiss(toast.id), 180);
  };

  const handleClose = () => {
    clearNotificationCallback(toast.actionCallbackId);
    setIsLeaving(true);
    setTimeout(() => onDismiss(toast.id), 180);
  };

  return (
    <div
      className={`${classes.toast} ${classes[toast.type] || classes.error} ${isLeaving ? classes.toastLeaving : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={classes.iconWrap}>
        <ToastIcon type={toast.type} />
      </div>
      <div className={classes.content}>
        {toast.title ? <p className={classes.title}>{toast.title}</p> : null}
        <p className={classes.message}>{toast.message}</p>
        {toast.actionLabel ? (
          <div className={classes.actions}>
            <button type="button" className={classes.actionButton} onClick={handleAction}>
              {toast.actionLabel}
            </button>
          </div>
        ) : null}
      </div>
      <button type="button" className={classes.closeButton} onClick={handleClose} aria-label="Schließen">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const dispatch = useDispatch();
  const toasts = useSelector((state) => state.notifications?.toasts || []);

  const handleDismiss = useCallback((id) => {
    dispatch(removeToast(id));
  }, [dispatch]);

  if (!toasts.length) return null;

  return createPortal(
    <div className={classes.container}>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
      ))}
    </div>,
    document.body
  );
};

export default ToastContainer;
